import type { DataItem } from '@pn/core/domain/data';
import {
  getIntelWorkspaceItemsForProcessing,
  getIntelWorkspaceItemsToRemove,
  getWorkspaceItemsToAddOrUpdate,
  type IntelWorkspaceItem,
} from '@pn/core/domain/intel/intel-workspace-item';
import type { LayerStyle } from '@pn/core/domain/layer';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { isNil, some } from 'lodash-es';
import { store } from 'src/application/storage/store';
import {
  intelWorkspaceItemsSlice,
  type IntelWorkspaceItemsState,
} from './intelWorkspaceItemsSlice';

export const useIntelWorkspaceItemsStorage = () => {
  const { name, getInitialState } = intelWorkspaceItemsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    areaFilterWorkspaceItemId: useSliceSelector(
      (state) => state.areaFilterWorkspaceItemId
    ),
    areaGeometryFilter: useSliceSelector((state) => state.areaGeometryFilter),
    /* Deprecated, stop using this. */
    provinceFilter: useSliceSelector((state) => state.provinceFilter),
    intelWorkspaceItems: useSliceSelector((state) => state.intelWorkspaceItems),
    mappableIntelWorkspaceItems: useSliceSelector(selectIntelMappableQueries),
    selectedIntelWorkspaceItem: useSliceSelector((state) =>
      state.intelWorkspaceItems.find(
        (query) => query.id === state.selectedIntelWorkspaceItemId
      )
    ),
    itemsToProcess: useSliceSelector(selectIntelQueriesToProcess),
    itemsToAddOrUpdateOnMap: useSliceSelector(
      selectIntelQueriesToAddOrUpdateOnMap
    ),
    itemsToRemoveFromMap: useSliceSelector(selectIntelQueriesToRemoveFromMap),
    isFetching: useSliceSelector(selectIntelQueriesThatAreFetching),
    ids: useSliceSelector(selectIntelQueriesIds),
  };
};

export const intelWorkspaceItemsActions = () => {
  const { dispatch, getState } = store;
  const { actions, name } = intelWorkspaceItemsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: (id: IntelWorkspaceItem['id']) => dispatch(actions.request(id)),
    receive: (id: IntelWorkspaceItem['id'], items: DataItem[]) =>
      dispatch(actions.receive({ id, items })),
    error: (id: IntelWorkspaceItem['id']) => dispatch(actions.error({ id })),
    retry: (id: IntelWorkspaceItem['id']) => dispatch(actions.retry(id)),
    select: (id: IntelWorkspaceItem['id']) => dispatch(actions.select(id)),
    unselect: () => dispatch(actions.unselect()),
    create: (item: IntelWorkspaceItem) => dispatch(actions.create(item)),
    add: (queries: IntelWorkspaceItem[]) => dispatch(actions.add(queries)),
    update: (updatedQuery: IntelWorkspaceItem) =>
      dispatch(actions.update(updatedQuery)),
    updateName: (id: string, name: string) =>
      dispatch(actions.updateName({ id, name })),
    updateFilter: (
      id: IntelWorkspaceItem['id'],
      filter: IntelWorkspaceItem['query']['filters'][0]
    ) => dispatch(actions.updateFilter({ id, filter })),
    updateFilters: (
      id: string,
      filters: IntelWorkspaceItem['query']['filters']
    ) => dispatch(actions.updateFilters({ id, filters })),
    updateSorts: (id: string, sorts: IntelWorkspaceItem['query']['sorts']) =>
      dispatch(actions.updateSorts({ id, sorts })),
    removeFilterByField: (id: string, field: string) =>
      dispatch(actions.removeFilterByField({ id, field })),
    remove: (id: IntelWorkspaceItem['id']) => dispatch(actions.remove(id)),
    toggleHideFromWorkspace: (id: string) =>
      dispatch(actions.toggleHideFromWorkspace(id)),
    checkAll: (id: string) => dispatch(actions.checkAll(id)),
    uncheckAll: (id: string) => dispatch(actions.uncheckAll(id)),
    check: (id: string, intelDataId: DataItem['_id']) =>
      dispatch(actions.check({ id, intelDataId })),
    uncheck: (id: string, intelDataId: DataItem['_id']) =>
      dispatch(actions.uncheck({ id, intelDataId })),
    clearWorkspace: () => dispatch(actions.clearWorkspace()),
    markAsVisuallyProcessed: (id: string) =>
      dispatch(actions.markAsVisuallyProcessed(id)),
    updateAreaFilter: (
      areaGeoJSON: GeoJSON.Geometry | null,
      workspaceItemId: string | null
    ) =>
      dispatch(
        actions.updateAreaFilter({ geometry: areaGeoJSON, workspaceItemId })
      ),
    updateProvinceFilter: (province: string | null) =>
      dispatch(actions.updateProvinceFilter(province)),
    clearGlobalFilters: () => dispatch(actions.clearGlobalFilters()),
    removeFromMap: (id: string) => dispatch(actions.removeFromMap(id)),
    removeByReportId: (reportId: string) =>
      dispatch(actions.removeByReportId(reportId)),
    updateAllLayerStyles: (itemId: WorkspaceItem['id'], style: LayerStyle) =>
      dispatch(actions.updateAllLayerStyles({ itemId, style })),
  };
};

const selectIntelMappableQueries = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => queries.filter((item) => !item.hideFromWorkspace)
);

const selectIntelQueriesToProcess = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => getIntelWorkspaceItemsForProcessing(queries)
);

const selectIntelQueriesToAddOrUpdateOnMap = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => getWorkspaceItemsToAddOrUpdate(queries)
);

const selectIntelQueriesToRemoveFromMap = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => getIntelWorkspaceItemsToRemove(queries)
);

const selectIntelQueriesThatAreFetching = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => some(queries, (item) => item.isFetching)
);
const selectIntelQueriesIds = createSelector(
  [(state: IntelWorkspaceItemsState) => state.intelWorkspaceItems],
  (queries) => queries.map((item) => item.id)
);
